/**
 * Leitura (§5.2.2, §5.2.3, §5.2.9): hash → `RouteState`. Função pura, sem acesso a `window`; nunca lança (passo 8).
 * Todo valor da URL é comparado com listas fechadas (slugs, UFs, códigos da Geography, números de candidato) antes de
 * uso (§5.2.10).
 */
import type { CandidateNumber, UfCode } from '../contracts/base';
import type { AbroadCity, CountryInfo, Geography, MunicipalityInfo } from '../contracts/static';
import type { MapMode, PlaceRoute, RouteOffice, RouteState } from '../model/types';
import { formatRoute, resolveDefaultCandidate } from './formatRoute';
import { isOfficeAvailable, screenAvailable } from './offices';
import {
  ABROAD_SLUG,
  CAND_RE,
  CITY_RE,
  COMMENTS_SLUG,
  HHMM_RE,
  IBGE_RE,
  ISO2_RE,
  SCREEN_PARAM,
  SLUG_TO_MODE,
  SLUG_TO_OFFICE,
  ufFromIbge,
  ufFromSlug,
  ZONE_RE,
} from './slugs';
import type { ParseResult, RouteContext } from './types';

/** Acima disto (sem o "#"), o hash é tratado como vazio (§5.2.2 passo 1). */
export const MAX_HASH_LENGTH = 256;
/** Faixa de `HH` em `t=HHMM` (§5.2.1): 17h00 do dia 25 até 23h59 do dia 26. */
const T_HH_MIN = 17;
const T_HH_MAX = 47;

// ── Índices da Geography (uma vez por objeto) ────────────────────────────────────────────────────────────────
interface GeoIndex {
  mun: ReadonlyMap<string, MunicipalityInfo>;
  city: ReadonlyMap<string, AbroadCity>;
  /** Chave em maiúsculas. */
  country: ReadonlyMap<string, CountryInfo>;
}
const indexCache = new WeakMap<Geography, GeoIndex>();

function geoIndex(geo: Geography): GeoIndex {
  let idx = indexCache.get(geo);
  if (!idx) {
    idx = {
      mun: new Map(geo.municipalities.map((m) => [m.ibge, m])),
      city: new Map(geo.abroad_cities.map((c) => [c.tse, c])),
      country: new Map(geo.countries.map((c) => [c.iso2.toUpperCase(), c])),
    };
    indexCache.set(geo, idx);
  }
  return idx;
}

// ── Lugar (§5.2.3) ───────────────────────────────────────────────────────────────────────────────────────────
interface PlaceOut {
  place: PlaceRoute;
  pending?: boolean;
  hold?: boolean;
}
const BR: PlaceRoute = Object.freeze({ kind: 'br' });

type Ctx = Pick<RouteContext, 'geo' | 'candidates' | 'flags' | 'governorUfs' | 'timeline' | 'nationalLeader'>;

/** 1º segmento que não é cargo mas é lugar: tolerância F02 (`#/sp` → `#/presidente/sp`). */
function isPlaceToken(seg: string): boolean {
  return seg === ABROAD_SLUG || ufFromSlug(seg) !== null || IBGE_RE.test(seg);
}

/** Município em cada cargo: Presidente sempre; Governadores só com F80 (P2); Senado e Deputados nunca. */
function municipalityAllowed(office: RouteOffice, flags: Ctx['flags']): boolean {
  if (office === 'president') return true;
  return office === 'governor' && flags.governor_municipal;
}

function parseAbroad(tail: readonly string[], office: RouteOffice, ctx: Ctx): PlaceOut {
  const geo = ctx.geo;
  if (office !== 'president' || !ctx.flags.abroad) return { place: BR };
  if (geo && geo.abroad_cities.length === 0) return { place: BR };
  const pending = geo == null;
  const root: PlaceRoute = { kind: 'abroad' };
  const s1 = tail[0];
  if (s1 === undefined) return { place: root, pending };
  if (ISO2_RE.test(s1)) {
    const iso2 = s1.toUpperCase();
    if (geo && !geoIndex(geo).country.has(iso2)) return { place: root };
    const country: PlaceRoute = { kind: 'country', iso2 };
    const s2 = tail[1];
    if (s2 === undefined || !CITY_RE.test(s2)) return { place: country, pending };
    if (geo) {
      const city = geoIndex(geo).city.get(s2);
      if (!city || city.country_iso2.toUpperCase() !== iso2) return { place: country };
    }
    return { place: { kind: 'abroad_city', iso2, tse: s2 }, pending };
  }
  if (CITY_RE.test(s1)) {
    // "exterior/cidade5": país inferido. Sem a Geography não há como inferir: fica a raiz e o hash espera (§5.2.9).
    if (!geo) return { place: root, pending: true, hold: true };
    const city = geoIndex(geo).city.get(s1);
    if (!city) return { place: root };
    return { place: { kind: 'abroad_city', iso2: city.country_iso2.toUpperCase(), tse: s1 } };
  }
  return { place: root, pending };
}

function parseUfTail(uf: UfCode, tail: readonly string[], office: RouteOffice, ctx: Ctx): PlaceOut {
  const geo = ctx.geo;
  const ufPlace: PlaceRoute = { kind: 'uf', uf };
  const s1 = tail[0];
  if (s1 === undefined || !IBGE_RE.test(s1) || !municipalityAllowed(office, ctx.flags)) return { place: ufPlace };
  let info: MunicipalityInfo | undefined;
  if (geo) {
    info = geoIndex(geo).mun.get(s1);
    if (!info || info.uf !== uf) return { place: ufPlace };
  } else if (ufFromIbge(s1) !== uf) {
    // O prefixo do IBGE já diz a UF: um município de outra UF cai sem esperar a Geography.
    return { place: ufPlace };
  }
  const pending = geo == null;
  const mun: PlaceRoute = { kind: 'municipality', uf, ibge: s1, zone: null };
  const zm = tail[1] === undefined ? null : ZONE_RE.exec(tail[1]);
  if (!zm || office !== 'president' || !ctx.flags.zones) return { place: mun, pending };
  const zone = Number(zm[1]);
  if (info && !info.zones.includes(zone)) return { place: mun };
  return { place: { kind: 'municipality', uf, ibge: s1, zone }, pending };
}

/** "ibge7" sem UF: UF inferida do município (só leitura). */
function parseBareMunicipality(ibge: string, office: RouteOffice, ctx: Ctx): PlaceOut {
  const geo = ctx.geo;
  const uf = geo ? (geoIndex(geo).mun.get(ibge)?.uf ?? null) : ufFromIbge(ibge);
  if (!uf) return { place: BR };
  const pending = geo == null;
  if (!municipalityAllowed(office, ctx.flags)) return { place: { kind: 'uf', uf }, pending };
  return { place: { kind: 'municipality', uf, ibge, zone: null }, pending };
}

function parsePlace(rest: readonly string[], office: RouteOffice, ctx: Ctx): PlaceOut {
  const s0 = rest[0];
  if (s0 === undefined) return { place: BR };
  if (s0 === ABROAD_SLUG) return parseAbroad(rest.slice(1), office, ctx);
  const uf = ufFromSlug(s0);
  if (uf) return parseUfTail(uf, rest.slice(1), office, ctx);
  if (IBGE_RE.test(s0)) return parseBareMunicipality(s0, office, ctx);
  return { place: BR };
}

// ── Parâmetros (§5.2.3) ──────────────────────────────────────────────────────────────────────────────────────
function parseReplay(value: string | null, ctx: Ctx): number | null {
  if (value == null || !ctx.flags.replay) return null;
  const m = HHMM_RE.exec(value);
  if (!m) return null;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (hh < T_HH_MIN || hh > T_HH_MAX || mm > 59) return null;
  const tMin = hh * 60 + mm;
  const w = ctx.timeline;
  if (!w) return tMin; // janela ainda desconhecida: vale a forma (§5.2.9)
  if (tMin < w.startTMin || tMin > w.endTMin) return null;
  if (w.liveTMin != null && tMin >= w.liveTMin) return null; // futuro → ao vivo
  return tMin;
}

/** Limpeza do passo 1: sem "#", ≤ 256, decodificado (falha ignorada), minúsculo, sem espaços nas pontas. */
function clean(hash: string): string {
  let s = hash.startsWith('#') ? hash.slice(1) : hash;
  if (s.length > MAX_HASH_LENGTH) return '';
  try {
    s = decodeURIComponent(s);
  } catch {
    // decodificação inválida: segue o texto bruto
  }
  return s.toLowerCase().trim();
}

function defaultRoute(prev: RouteState | null): RouteState {
  return {
    office: 'president',
    place: BR,
    mapMode: 'municipalities',
    mapCandidate: null,
    replayTMin: null,
    tvOnEntry: prev?.tvOnEntry ?? false,
  };
}

function parseUnsafe(hash: string, ctx: Ctx, prev: RouteState | null): ParseResult {
  // 1–2. Limpeza e partição (primeiro "?"; segmentos vazios descartados; parâmetro repetido: vale o primeiro).
  const s = clean(hash);
  const qi = s.indexOf('?');
  const segs = (qi < 0 ? s : s.slice(0, qi)).split('/').filter((x) => x !== '');
  const params = new URLSearchParams(qi < 0 ? '' : s.slice(qi + 1));

  // 3. Cargo (com a tolerância de lugar sem cargo).
  let office: RouteOffice = 'president';
  let rest: readonly string[] = [];
  const first = segs[0];
  if (first !== undefined) {
    const slugOffice = SLUG_TO_OFFICE.get(first);
    if (slugOffice) {
      office = slugOffice;
      rest = segs.slice(1);
    } else if (isPlaceToken(first)) {
      rest = segs;
    }
  }

  // 4. Disponibilidade: cargo indisponível → Presidente, mantendo o lugar válido para Presidente.
  let pending = false;
  if (!isOfficeAvailable(office, ctx)) office = 'president';
  else if (office === 'governor' && ctx.candidates == null) pending = true;

  // 5. Lugar, nível a nível.
  const placeOut = parsePlace(rest, office, ctx);
  pending ||= placeOut.pending === true;

  // 6. Parâmetros. `mapa`/`cand` só com Presidente; nos outros cargos, a memória vem de `prev` (§5.4.3).
  let mapMode: MapMode = prev?.mapMode ?? 'municipalities';
  let mapCandidate: CandidateNumber | null = prev?.mapCandidate ?? null;
  if (office === 'president') {
    const slug = params.get('mapa');
    const asked = slug == null ? undefined : SLUG_TO_MODE.get(slug);
    mapMode = asked && (asked !== 'advantage' || ctx.flags.advantage_mode) ? asked : 'municipalities';
    mapCandidate = null;
    if (mapMode === 'candidate') {
      const cand = params.get('cand');
      if (cand != null && CAND_RE.test(cand)) {
        if (ctx.candidates) {
          if (ctx.candidates.president.some((c) => c.number === cand)) mapCandidate = cand;
        } else {
          mapCandidate = cand; // cadastro ainda não chegou: vale a forma (§5.2.9)
          pending = true;
        }
      }
      // A resolução do `cand` ausente acontece uma vez e fica na rota (§5.2.4).
      mapCandidate ??= resolveDefaultCandidate(ctx);
    }
  }
  const replayTMin = parseReplay(params.get('t'), ctx);
  // `tv=1` só na montagem (sem `prev`); depois, o valor da montagem segue (§5.2.7).
  const tvOnEntry = prev ? prev.tvOnEntry : params.get('tv') === '1' && ctx.flags.tv;

  const route: RouteState = { office, place: placeOut.place, mapMode, mapCandidate, replayTMin, tvOnEntry };
  if (params.get(SCREEN_PARAM) === COMMENTS_SLUG && screenAvailable(ctx)) route.screen = 'comments';
  // 7. Canonização.
  const canonical = formatRoute(route, ctx);
  return { route, canonical, changed: hash !== canonical, pending, holdHash: placeOut.hold === true };
}

/**
 * §5.2.2. `prev = null` na montagem (lê `tv=1`); nos demais casos, a rota atual (memória do modo do mapa e
 * `tvOnEntry`). Qualquer exceção → Presidente · Brasil com os padrões e `changed = true` (passo 8).
 */
export function parseRoute(hash: string, ctx: Ctx, prev: RouteState | null = null): ParseResult {
  try {
    return parseUnsafe(hash, ctx, prev);
  } catch {
    const route = defaultRoute(prev);
    let canonical = '#/presidente';
    try {
      canonical = formatRoute(route, ctx);
    } catch {
      // fica o padrão literal
    }
    return { route, canonical, changed: true, pending: false, holdHash: false };
  }
}

/**
 * Normaliza uma rota montada pelas ações com as MESMAS regras da leitura (§5.3.1): `parseRoute(formatRoute(r))`, com a
 * própria rota como `prev` (guarda a memória do modo do mapa e `tvOnEntry`).
 */
export function normalizeRoute(r: RouteState, ctx: Ctx): ParseResult {
  return parseRoute(formatRoute(r, ctx), ctx, r);
}
